/**
 * Multi-item ("Mixed") editing helpers for the design studio.
 *
 * Ported from pages/projects2/-bulk-edit-form.vue so DesignStudioItemForm can
 * edit a whole selection iTunes-style: value resolvers report the shared value
 * across items or flag it mixed, and staging mappers turn one form change into
 * per-item patches ([{ itemId, patch }], where patch is partial top-level
 * fields or a FULL config_data replacement — never deep-merged).
 *
 * Selection gesture helpers mirror utils/itemEditSession.js semantics so a
 * later engine adoption is a drop-in.
 */
import { groupBy } from 'lodash'
import { configForSubstrate, validateItemAvailability, preserveFinishedSize, resolveSizingIntent, itemSizingKey } from './designStudioSizing.js'
import { getItemAspectRatio } from './sizingPolicy.js'
import { buildRendererPropsFromItem } from './dsItemRendererProps.js'
import { resolveDesignStudioItemSceneMetrics } from './designStudioItemScene.js'
import { isMatColorCategory, resolveMatSelection } from './matPolicy.js'

export function stableEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function cloneJson(value) {
  if (value == null) return value
  return JSON.parse(JSON.stringify(value))
}

export function isMiscCategory(category) {
  return String(category || '').trim().toLowerCase() === 'misc'
}

// --- value resolution --------------------------------------------------------
// Every resolver returns { value, mixed } so the form has one display contract.

export function resolveShared(values = [], { emptyValue = '' } = {}) {
  if (!values.length) return { value: emptyValue, mixed: false }
  const mixed = values.length > 1 && !values.every(v => stableEqual(v, values[0]))
  return { value: mixed ? emptyValue : values[0], mixed }
}

export function sharedTopLevel(items = [], key) {
  return resolveShared(items.map(item => item?.[key] ?? ''))
}

export function sharedConfigField(items = [], key) {
  return resolveShared(items.map(item => item?.config_data?.[key] ?? ''))
}

export function sharedSubstrateName(items = []) {
  return resolveShared(items.map(item => item?.config_data?.substrate || ''))
}

// chosen_options normal form: { name, value, option_id, extra_data }
export function sharedOptionId(items = [], category) {
  const values = items.map(item =>
    (item?.config_data?.chosen_options || []).find(o => o?.name === category)?.option_id ?? null
  )
  if (!values.length) return { value: null, mixed: false }
  const mixed = values.length > 1 && !values.every(v => String(v) === String(values[0]))
  return { value: mixed ? null : values[0], mixed }
}

// Categories chosen on EVERY item (intersection), misc excluded
export function commonChosenCategories(items = []) {
  if (!items.length) return []
  const sets = items.map(item => new Set(
    (item?.config_data?.chosen_options || [])
      .filter(o => !isMiscCategory(o?.name))
      .map(o => o.name)
  ))
  const [first, ...rest] = sets
  return Array.from(first || []).filter(category => rest.every(set => set.has(category)))
}

// --- selected-options <-> chosen-options plumbing ----------------------------

export function selectedOptionsMap(chosenOptions = []) {
  const selected = {}
  ;(chosenOptions || []).forEach(option => {
    if (!isMiscCategory(option?.name) && option?.option_id) {
      selected[option.name] = option.option_id
    }
  })
  return selected
}

export function chosenOptionFromAvailable(category, opt) {
  return {
    name: category,
    value: opt.value ?? null,
    option_id: opt.id,
    extra_data: opt.extra_data || null,
  }
}

export function chosenOptionsFromSelected(selected, existingChosenOptions = [], availableOptions = []) {
  const chosen = Object.entries(selected)
    .map(([category, id]) => {
      const opt = availableOptions.find(option => String(option.id) === String(id))
      if (!opt) return null
      return chosenOptionFromAvailable(category, opt)
    })
    .filter(Boolean)

  const misc = (existingChosenOptions || []).filter(option => isMiscCategory(option?.name))
  return [...chosen, ...misc]
}

/** Select the defaults and their specified options before counting mats. */
export function defaultSelectedOptions(availableOptions = []) {
  const grouped = groupBy(availableOptions, 'name')
  const selected = {}
  Object.entries(grouped).forEach(([category, options]) => {
    if (!isMiscCategory(category)) selected[category] = options.find(option => option.is_default)?.id || null
  })
  const defaults = availableOptions.filter(option => selected[option.name] === option.id)
    .flatMap(option => option.default_options || [])
  for (const { category, id } of defaults) {
    if (!category || !id || isMiscCategory(category) || selected[category]) continue
    const option = availableOptions.find(option => String(option.id) === String(id) && option.name === category)
    if (option) selected[category] = option.id
  }
  return selected
}

export function defaultChosenOptions(availableOptions = [], substrateName) {
  const selected = defaultSelectedOptions(availableOptions)
  const chosen = chosenOptionsFromSelected(selected, [], availableOptions)
  const substrateKey = String(substrateName || '').trim().toLowerCase()
  const misc = (availableOptions || [])
    .filter(option => isMiscCategory(option?.name))
    .filter(option => String(option?.substrate || '').trim().toLowerCase() === substrateKey)
    .map(option => chosenOptionFromAvailable(option.name, option))
  return [...chosen, ...misc]
}

// Mutates `selected`: clears categories excluded by the picked option, then
// fills that option's defaults into still-empty categories.
export function applyOptionSideEffects(selected, selectedOption, availableOptions = []) {
  const excluded = Array.isArray(selectedOption?.excluded_options) ? selectedOption.excluded_options : []
  const excludedIds = new Set(excluded.map(option => String(option?.id)).filter(Boolean))
  Object.entries(selected).forEach(([category, id]) => {
    if (String(id) === String(selectedOption?.id)) return
    if (excludedIds.has(String(id))) {
      selected[category] = null
    }
  })
  ;(selectedOption?.default_options || []).forEach(defaultOption => {
    if (!defaultOption?.category || !defaultOption?.id) return
    if (selected[defaultOption.category]) return
    const exists = availableOptions.find(option => String(option.id) === String(defaultOption.id))
    if (exists) selected[defaultOption.category] = defaultOption.id
  })
}

// --- artwork transform clamp (pure port of design-studio clampedArtworkPatch) -

export function normalizeSignedScale(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return 1
  const clamped = Math.abs(n) >= 1 ? n : (n < 0 ? -1 : 1)
  return Math.round(clamped * 100) / 100
}

export function normalizeArtworkRotation(value) {
  const n = Number(value) || 0
  const normalized = ((Math.round(n / 90) * 90) % 360 + 360) % 360
  return [0, 90, 180, 270].includes(normalized) ? normalized : 0
}

/**
 * Re-clamp an item's existing artwork transform to new art-opening dims.
 * Returns {} when the item has no transform (and never had one) so patches
 * don't introduce artwork keys the committed baseline lacks.
 */
export function clampArtworkForSize(item, { width, height } = {}) {
  const cfg = item?.config_data || {}
  const sx = normalizeSignedScale(cfg.artwork_scale_x != null ? Number(cfg.artwork_scale_x) : 1)
  const sy = normalizeSignedScale(cfg.artwork_scale_y != null ? Number(cfg.artwork_scale_y) : sx)
  const rotation = normalizeArtworkRotation(cfg.artwork_rotation)
  let offsetLeft = Math.round((Number(cfg.artwork_offset_left) || 0) * 100) / 100
  let offsetTop = Math.round((Number(cfg.artwork_offset_top) || 0) * 100) / 100

  const hasExistingKeys = ['artwork_scale_x', 'artwork_scale_y', 'artwork_rotation', 'artwork_offset_left', 'artwork_offset_top']
    .some(key => cfg[key] != null)
  const isActive = sx !== 1 || sy !== 1 || rotation !== 0 || offsetLeft !== 0 || offsetTop !== 0
  if (!hasExistingKeys && !isActive) return {}

  const artW = parseFloat(width) || parseFloat(cfg.width) || 16
  const artH = parseFloat(height) || parseFloat(cfg.height) || 20
  const imgAspect = getItemAspectRatio(item?.imagedata) || (artW / artH)
  if (Number.isFinite(artW) && Number.isFinite(artH) && artW > 0 && artH > 0
    && Number.isFinite(imgAspect) && imgAspect > 0) {
    const effectiveAspect = rotation % 180 === 90 ? 1 / imgAspect : imgAspect
    const baseCoverW = Math.max(artW, artH * effectiveAspect)
    const baseCoverH = baseCoverW / effectiveAspect
    const uw = baseCoverW * Math.abs(sx)
    const uh = baseCoverH * Math.abs(sy)
    const maxOffsetLeft = (uw - artW) / 2
    const maxOffsetTop = (uh - artH) / 2
    offsetLeft = Math.round(Math.min(Math.max(-maxOffsetLeft, offsetLeft), maxOffsetLeft) * 100) / 100
    offsetTop = Math.round(Math.min(Math.max(-maxOffsetTop, offsetTop), maxOffsetTop) * 100) / 100
  }

  return {
    artwork_scale_x: sx,
    artwork_scale_y: sy,
    artwork_rotation: rotation,
    artwork_offset_left: offsetLeft,
    artwork_offset_top: offsetTop,
  }
}

// --- per-item staging mappers -------------------------------------------------
// All return [{ itemId, patch }]; unchanged items are dropped.

export function stageTopLevelEdits(items = [], key, value) {
  return items.map(item => {
    const oldVal = item?.[key] ?? ''
    if (stableEqual(oldVal, value)) return null
    return { itemId: item.id, patch: { [key]: value } }
  }).filter(Boolean)
}

export function stageConfigEdits(items = [], mapper, sizing = null) {
  return items.map(item => {
    const oldConfig = cloneJson(item?.config_data || {})
    let newConfig = mapper(cloneJson(oldConfig), item)
    if (sizing?.target === 'finished') {
      newConfig = preserveFinishedSize(item, newConfig, { ...sizing, sizes: sizing.sizesByItem?.[itemSizingKey(item)] || {} })
      newConfig = { ...newConfig, ...clampArtworkForSize(item, newConfig) }
    }
    if (['substrate', 'width', 'height'].some(key => oldConfig[key] !== newConfig[key])) {
      validateItemAvailability({ ...item, config_data: newConfig })
    }
    if (stableEqual(oldConfig, newConfig)) return null
    return { itemId: item.id, patch: { config_data: newConfig } }
  }).filter(Boolean)
}

function normalizedCategory(value) {
  return String(value || '').trim().toLowerCase()
}

function optionsForSubstrate(optionsBySubstrate = {}, substrateName) {
  const key = Object.keys(optionsBySubstrate)
    .find(name => normalizedCategory(name) === normalizedCategory(substrateName))
  return key ? optionsBySubstrate[key] : []
}

function equivalentOption(sourceSelection, sourceCatalog = [], targetCatalog = []) {
  const category = normalizedCategory(sourceSelection?.name)
  const candidates = targetCatalog.filter(option => normalizedCategory(option?.name) === category)
  if (!candidates.length) return null

  const exact = candidates.find(option => String(option.id) === String(sourceSelection.option_id))
  if (exact) return exact

  const sourceDefinition = sourceCatalog.find(option => String(option.id) === String(sourceSelection.option_id))
  const sourceValue = sourceDefinition ? sourceDefinition.value : sourceSelection.value
  return candidates.find(option => stableEqual(option.value, sourceValue))
    || (sourceDefinition
      && !stableEqual(sourceDefinition.value, sourceSelection.value)
      && candidates.length === 1
      ? candidates[0]
      : null)
}

/**
 * Copy selected configuration categories from one item to several targets.
 * Unsupported target-substrate options are left unchanged and reported.
 */
export function buildCopiedConfigEdits({
  source,
  targets = [],
  categoryNames = [],
  applySubstrate = false,
  optionsBySubstrate = {},
} = {}) {
  if (!source) return { edits: [], skipped: [] }
  const sourceConfig = source.config_data || {}
  const sourceSubstrate = sourceConfig.substrate || source.substrate || ''
  const sourceCatalog = optionsForSubstrate(optionsBySubstrate, sourceSubstrate)
  const selectedCategories = new Set(categoryNames.map(normalizedCategory))
  const sourceSelections = (sourceConfig.chosen_options || [])
    .filter(option => !isMiscCategory(option?.name))
    .filter(option => selectedCategories.has(normalizedCategory(option?.name)))
  const skipped = []

  const edits = targets.map(target => {
    const oldConfig = cloneJson(target.config_data || {})
    const targetSubstrate = applySubstrate
      ? sourceSubstrate
      : (oldConfig.substrate || target.substrate || '')
    const targetCatalog = optionsForSubstrate(optionsBySubstrate, targetSubstrate)
    let chosenOptions = applySubstrate
      ? defaultChosenOptions(targetCatalog, targetSubstrate)
      : cloneJson(oldConfig.chosen_options || [])
    const selected = selectedOptionsMap(chosenOptions)
    const copiedValues = new Map()

    sourceSelections.forEach(sourceSelection => {
      const targetOption = equivalentOption(sourceSelection, sourceCatalog, targetCatalog)
      if (!targetOption) {
        skipped.push({ itemId: target.id, category: sourceSelection.name })
        return
      }
      selected[targetOption.name] = targetOption.id
      applyOptionSideEffects(selected, targetOption, targetCatalog)
      copiedValues.set(String(targetOption.id), sourceSelection)
    })

    chosenOptions = chosenOptionsFromSelected(selected, chosenOptions, targetCatalog)
      .map(option => {
        const sourceSelection = copiedValues.get(String(option.option_id))
        if (!sourceSelection) return option
        return {
          ...option,
          value: cloneJson(sourceSelection.value),
          extra_data: cloneJson(sourceSelection.extra_data) || option.extra_data,
        }
      })

    const newConfig = {
      ...oldConfig,
      ...(applySubstrate ? { substrate: sourceSubstrate } : {}),
      chosen_options: chosenOptions,
    }
    if (stableEqual(oldConfig, newConfig)) return null
    return { itemId: target.id, patch: { config_data: newConfig } }
  }).filter(Boolean)

  return { edits, skipped }
}

/**
 * Project a form patch onto an item, including derived fields used by the
 * board. Hover previews and committed edits share this path so their aspect
 * ratio and substrate aliases cannot drift.
 */
export function projectFormItemPatch(item, patch = {}) {
  const nextPatch = { ...patch }
  if (!patch.config_data) return nextPatch
  if (Object.prototype.hasOwnProperty.call(patch.config_data, 'substrate')) {
    nextPatch.substrate = patch.config_data.substrate
  }
  const nextItem = { ...item, config_data: patch.config_data }
  const metrics = resolveDesignStudioItemSceneMetrics({
    rendererProps: buildRendererPropsFromItem(nextItem),
  })
  nextPatch.width = Number((parseFloat(metrics.outerWidth) || parseFloat(patch.config_data.width) || item.width).toFixed(2))
  nextPatch.height = Number((parseFloat(metrics.outerHeight) || parseFloat(patch.config_data.height) || item.height).toFixed(2))
  return nextPatch
}

/** Convert form-style item patches into the session's flat edit contract. */
export function sessionEditsFromPatches(session, edits = []) {
  return edits.flatMap(({ itemId, patch }) => {
    const item = session.getItem(itemId)
    if (!item || !patch) return []
    const nextPatch = projectFormItemPatch(item, patch)
    return Object.entries(nextPatch).map(([field, newVal]) => ({
      rowId: item.id,
      field,
      oldVal: item[field],
      newVal,
    }))
  })
}

// New substrate: per-item defaults rebuilt for it; each item keeps its own
// art dims / artwork transform / other scalar config keys.
export function stageSubstrateEdits(items = [], { substrateName, availableOptions = [], sizing = null } = {}) {
  return stageConfigEdits(items, (config, item) => {
    config = configForSubstrate(item, substrateName)
    const { chosenOptions } = resolveMatSelection({
      width: config.width,
      height: config.height,
      substrate: substrateName,
      chosenOptions: defaultChosenOptions(availableOptions, substrateName),
      availableOptions,
    })
    return {
      ...config,
      substrate: substrateName,
      chosen_options: chosenOptions,
    }
  }, sizing)
}

// option = available-option object, or null to clear the category
export function stageOptionEdits(items = [], { category, option = null, availableOptions = [], sizing = null } = {}) {
  const normalizedCategory = String(category || '').trim().toLowerCase()
  const catalogHasSubstrates = availableOptions.some(candidate => candidate?.substrate)
  return stageConfigEdits(items, (config, item) => {
    const substrateName = String(
      config?.substrate || item?.substrate || ''
    ).trim().toLowerCase()
    const itemOptions = catalogHasSubstrates
      ? availableOptions.filter(candidate => (
        String(candidate?.substrate || '').trim().toLowerCase() === substrateName
      ))
      : availableOptions
    const categoryOptions = itemOptions.filter(candidate => (
      String(candidate?.name || '').trim().toLowerCase() === normalizedCategory
    ))
    // A union catalog is used for mixed-substrate editing. Categories that do
    // not exist for this item's substrate are display-only for the other rows.
    if (!categoryOptions.length) return config

    let itemOption = null
    if (option) {
      itemOption = categoryOptions.find(candidate => String(candidate.id) === String(option.id))
        || categoryOptions.find(candidate => stableEqual(candidate.value, option.value)
          && (!isMatColorCategory(category) || (candidate.extra_data?.material || '') === (option.extra_data?.material || '')))
      // The chosen catalog value has no equivalent on this substrate.
      if (!itemOption) return config
    }
    const selected = selectedOptionsMap(config.chosen_options)
    selected[category] = itemOption ? itemOption.id : null
    if (itemOption) applyOptionSideEffects(selected, itemOption, itemOptions)
    const chosenOptions = chosenOptionsFromSelected(selected, config.chosen_options, itemOptions)
    return {
      ...config,
      chosen_options: sizing?.target !== 'finished' && itemOption && isMatColorCategory(category) ? resolveMatSelection({
        width: config.width, height: config.height, substrate: config.substrate || item.substrate,
        chosenOptions, availableOptions: itemOptions,
      }).chosenOptions : chosenOptions,
    }
  }, sizing)
}

// Art-opening size, staged as strings (config_data convention); an undefined
// dimension keeps each item's own value. Items that already carry an artwork
// transform get it re-clamped to the new dims.
export function stageSizeEdits(items = [], { width, height, availableOptions = [], sizing = null, aspect, swap = false } = {}) {
  return stageConfigEdits(items, (config, item) => {
    const resolved = sizing ? resolveSizingIntent(item, {
      ...sizing, width, height, aspect, swap,
      ratio: sizing.locked ? sizing.ratios?.[itemSizingKey(item)] : null,
      sizes: sizing.sizesByItem?.[itemSizingKey(item)] || {},
    }) : null
    if (aspect === 'current') return config
    const nextWidth = resolved?.width ?? (width !== undefined ? String(width) : config.width)
    const nextHeight = resolved?.height ?? (height !== undefined ? String(height) : config.height)
    let next = {
      ...config,
      width: nextWidth,
      height: nextHeight,
      chosen_options: resolveMatSelection({
        width: nextWidth, height: nextHeight,
        substrate: config.substrate || item.substrate,
        chosenOptions: config.chosen_options,
        availableOptions,
      }).chosenOptions,
    }
    if (sizing?.target === 'finished') {
      next = preserveFinishedSize({ ...item, config_data: resolved }, next, {
        ...sizing, sizes: sizing.sizesByItem?.[itemSizingKey(item)] || {},
      })
    }
    return { ...next, ...clampArtworkForSize(item, { width: next.width, height: next.height }) }
  })
}

export function matRemovalMessage(items = [], edits = []) {
  const count = config => (config?.chosen_options || []).filter(option => isMatColorCategory(option.name)).length
  const before = new Map(items.map(item => [String(item.id), count(item.config_data)]))
  const removals = edits.map(({ itemId, patch }) =>
    (before.get(String(itemId)) || 0) - count(patch.config_data)
  ).filter(removed => removed > 0)
  const total = removals.reduce((sum, removed) => sum + removed, 0)
  if (!total) return null
  return `${total === 1 ? 'A mat was' : `${total} mats were`} removed from ${removals.length} ${removals.length === 1 ? 'item' : 'items'} because the finished ${removals.length === 1 ? 'piece would' : 'pieces would'} be too large.`
}

export function optionRemovalMessage(items = [], edits = []) {
  const before = new Map(items.map(item => [String(item.id), item.config_data?.chosen_options || []]))
  const removals = edits.map(({ itemId, patch }) => {
    const remaining = new Set((patch.config_data?.chosen_options || []).map(option => option.name))
    return (before.get(String(itemId)) || []).filter(option => !remaining.has(option.name))
  }).filter(options => options.length)
  if (!removals.length) return null
  const categories = [...new Set(removals.flat().map(option => option.name))]
  return `${categories.join(', ')} ${categories.length === 1 ? 'was' : 'were'} removed from ${removals.length} ${removals.length === 1 ? 'item' : 'items'} because ${categories.length === 1 ? 'it conflicts' : 'they conflict'} with the selected option.`
}

export function findClosestMatWidthId(options = [], target) {
  return options.reduce((best, option) => {
    const value = parseFloat(option.value)
    if (isNaN(value)) return best
    if (!best) return option
    return Math.abs(value - target) < Math.abs(parseFloat(best.value) - target) ? option : best
  }, null)?.id ?? null
}

// matWidthGrouped: { [matWidthCategory]: options[] } — closest option per side
export function stageMatWidthSliderEdits(items = [], { value, matWidthGrouped = {}, availableOptions = [], sizing = null } = {}) {
  const catalogHasSubstrates = availableOptions.some(option => option?.substrate)
  return stageConfigEdits(items, (config, item) => {
    const substrateName = String(config?.substrate || item?.substrate || '').trim().toLowerCase()
    const itemOptions = catalogHasSubstrates
      ? availableOptions.filter(option => String(option?.substrate || '').trim().toLowerCase() === substrateName)
      : availableOptions
    const itemOptionIds = new Set(itemOptions.map(option => String(option.id)))
    const selected = selectedOptionsMap(config.chosen_options)
    Object.entries(matWidthGrouped).forEach(([category, options]) => {
      const id = findClosestMatWidthId(
        options.filter(option => itemOptionIds.has(String(option.id))),
        value,
      )
      if (id !== null) selected[category] = id
    })
    const chosenOptions = chosenOptionsFromSelected(selected, config.chosen_options, itemOptions)
    return {
      ...config,
      chosen_options: sizing?.target !== 'finished' && chosenOptions.some(option => isMatColorCategory(option.name)) ? resolveMatSelection({
        width: config.width,
        height: config.height,
        substrate: config.substrate || item.substrate,
        chosenOptions,
        availableOptions: itemOptions,
      }).chosenOptions : chosenOptions,
    }
  }, sizing)
}

// --- selection gestures (mirror itemEditSession semantics on plain arrays) ---

export function toggleSelectedId(ids = [], id) {
  const strId = String(id)
  const next = ids.some(existing => String(existing) === strId)
    ? ids.filter(existing => String(existing) !== strId)
    : [...ids, id]
  return { ids: next, anchorId: id }
}

// Shift-click: extend from anchorId (fallback: last selected) to id within the
// given visible order; the range is ADDED to the selection.
export function rangeSelectIds(ids = [], anchorId, id, orderedIds = []) {
  const from = anchorId ?? ids[ids.length - 1]
  const fromIdx = orderedIds.findIndex(o => String(o) === String(from))
  const toIdx = orderedIds.findIndex(o => String(o) === String(id))
  if (fromIdx === -1 || toIdx === -1) {
    const merged = ids.some(existing => String(existing) === String(id)) ? ids : [...ids, id]
    return { ids: merged, anchorId: id }
  }
  const [lo, hi] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx]
  const range = orderedIds.slice(lo, hi + 1)
  const seen = new Set(ids.map(String))
  const merged = [...ids]
  range.forEach(rangeId => {
    if (!seen.has(String(rangeId))) {
      seen.add(String(rangeId))
      merged.push(rangeId)
    }
  })
  return { ids: merged, anchorId }
}
